import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './FloatingChatBot.css';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const FloatingChatBot: React.FC = () => {
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'assistant',
      content: "Hello! I'm Maxel Assistant, your AI-powered 3D printing expert. I can help you with print settings, troubleshooting, material selection, and optimization tips. How can I assist you today?",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Load chat history for authenticated users
  useEffect(() => {
    if (currentUser) {
      const savedMessages = localStorage.getItem(`chat_history_${currentUser.uid}`);
      if (savedMessages) {
        try {
          const parsedMessages = JSON.parse(savedMessages).map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          setMessages(parsedMessages);
        } catch (error) {
          console.error('Failed to load chat history:', error);
        }
      }
    }
  }, [currentUser]);

  // Save chat history for authenticated users
  useEffect(() => {
    if (currentUser && messages.length > 1) { // Don't save just the initial message
      localStorage.setItem(`chat_history_${currentUser.uid}`, JSON.stringify(messages));
    }
  }, [messages, currentUser]);

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;
    
    const newUserMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setInputMessage('');
    setIsTyping(true);
    
    // Simulate AI response (replace with actual API call)
    setTimeout(() => {
      const aiResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: "I understand you're looking for help with that. Let me provide you with some guidance based on your question. This is a placeholder response that will be replaced with actual AI functionality.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    if (isOpen) {
      // Start closing animation
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 300); // Match animation duration
    } else {
      setIsOpen(true);
    }
  };

  const handleCloseChat = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300); // Match animation duration
  };

  return (
    <>
      {/* Floating Button - Only show when chat is closed */}
      {!isOpen && (
        <div className="floating-chat-button" onClick={toggleChat}>
          <img src="/ai-assistant-icon.png" alt="AI Assistant" width={24} height={24} />
        </div>
      )}

      {/* Chat Modal */}
      {isOpen && (
        <>
          <div className="floating-chat-overlay" onClick={handleCloseChat}></div>
          <div className={`floating-chat-modal ${isClosing ? 'closing' : ''}`}>
            <div className="floating-chat-header">
              <div className="chat-header-info">
                <img src="/ai-assistant-icon.png" alt="AI Assistant" width={32} height={32} />
                <div>
                  <h3>Maxel Assistant</h3>
                  <span className="chat-status">
    
                  </span>
                </div>
              </div>
              <button className="close-chat-button" onClick={handleCloseChat}>
                ×
              </button>
            </div>

            <div className="floating-chat-content">
              <div className="chat-messages">
                {messages.map((message) => (
                  <div key={message.id} className={`message ${message.type}`}>
                    <div className="message-avatar">
                      {message.type === 'assistant' ? (
                        <img src="/ai-assistant-icon.png" alt="AI" width={32} height={32} />
                      ) : (
                        <div className="user-avatar">U</div>
                      )}
                    </div>
                    <div className="message-content">
                      <div className="message-text">{message.content}</div>
                      <div className="message-timestamp">
                        {message.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isTyping && (
                  <div className="message assistant typing">
                    <div className="message-avatar">
                      <img src="/ai-assistant-icon.png" alt="AI" width={32} height={32} />
                    </div>
                    <div className="message-content">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="floating-chat-input">
                <div className="suggested-questions">
                  <button className="suggestion-chip" onClick={() => setInputMessage('How do I fix layer adhesion issues?')}>
                    Print quality issues
                  </button>
                  <button className="suggestion-chip" onClick={() => setInputMessage('What material should I use for outdoor prints?')}>
                    Material recommendations
                  </button>
                  <button className="suggestion-chip" onClick={() => setInputMessage('How do I optimize support settings?')}>
                    Support settings
                  </button>
                </div>
                
                <div className="chat-input-wrapper">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about print settings, troubleshooting, materials..."
                    className="chat-input"
                    rows={1}
                  />
                  <button 
                    onClick={handleSendMessage}
                    className="send-button"
                    disabled={!inputMessage.trim()}
                  >
                    ↗
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FloatingChatBot; 